/**
 * Constantes et mappings pour le module RH
 * Labels lisibles pour les valeurs d'énumération
 */

// Types de contrat
export const CONTRACT_TYPES = {
  cdi: "CDI",
  cdd: "CDD",
  alternance: "Alternance",
  stage: "Stage",
  interim: "Intérim",
};

// Statuts employé
export const EMPLOYEE_STATUS = {
  cadre: "Cadre",
  non_cadre: "Non-cadre",
  etam: "ETAM",
};

// Types de temps de travail
export const WORK_TIME_TYPES = {
  full_time: "Temps plein",
  part_time: "Temps partiel",
};

// Motifs de sortie
export const TERMINATION_REASONS = {
  resignation: "Démission",
  dismissal: "Licenciement",
  conventional_break: "Rupture conventionnelle",
  end_of_cdd: "Fin de CDD",
  retirement: "Retraite",
  other: "Autre",
};

// Situations familiales
export const FAMILY_STATUS = {
  single: "Célibataire",
  married: "Marié(e)",
  pacs: "Pacsé(e)",
  divorced: "Divorcé(e)",
  widowed: "Veuf/Veuve",
};

// Statuts du salarié (active/terminated)
export const EMPLOYEE_RECORD_STATUS = {
  active: "Actif",
  terminated: "Sorti",
};

// Catégories de documents
export const DOCUMENT_CATEGORIES = {
  id_card: "Carte d'identité",
  cni: "Carte d'identité",
  passport: "Passeport",
  residence_permit: "Titre de séjour",
  rib: "RIB",
  cv: "CV",
  cover_letter: "Lettre de motivation",
  vital_card: "Carte vitale",
  diploma: "Diplôme",
  driving_license: "Permis de conduire",
  proof_of_address: "Justificatif de domicile",
  other: "Autre",
};

// Couleurs des badges par catégorie de document
export const DOCUMENT_CATEGORY_COLORS = {
  id_card: "bg-blue-50 text-blue-700 border-blue-200",
  cni: "bg-blue-50 text-blue-700 border-blue-200",
  passport: "bg-indigo-50 text-indigo-700 border-indigo-200",
  residence_permit: "bg-purple-50 text-purple-700 border-purple-200",
  rib: "bg-emerald-50 text-emerald-700 border-emerald-200",
  cv: "bg-cyan-50 text-cyan-700 border-cyan-200",
  cover_letter: "bg-teal-50 text-teal-700 border-teal-200",
  vital_card: "bg-green-50 text-green-700 border-green-200",
  diploma: "bg-amber-50 text-amber-700 border-amber-200",
  driving_license: "bg-orange-50 text-orange-700 border-orange-200",
  proof_of_address: "bg-slate-50 text-slate-700 border-slate-200",
  other: "bg-gray-50 text-gray-700 border-gray-200",
};

// Contrats à durée déterminée (nécessitent une date de fin)
export const FIXED_TERM_CONTRACTS = ["cdd", "alternance", "stage", "interim"];

// Types de documents contractuels
export const CONTRACT_DOC_TYPES = {
  initial_contract: "Contrat initial",
  amendment: "Avenant",
  job_offer: "Promesse d'embauche",
  resignation_letter: "Lettre de démission",
  termination_agreement: "Rupture conventionnelle",
  other: "Autre",
};

// Couleurs des badges par type de contrat
export const CONTRACT_DOC_TYPE_COLORS = {
  initial_contract: "bg-blue-100 text-blue-700 border-blue-200",
  amendment: "bg-purple-100 text-purple-700 border-purple-200",
  job_offer: "bg-cyan-100 text-cyan-700 border-cyan-200",
  resignation_letter: "bg-orange-100 text-orange-700 border-orange-200",
  termination_agreement: "bg-red-100 text-red-700 border-red-200",
  other: "bg-slate-100 text-slate-700 border-slate-200",
};

export function getContractDocTypeLabel(value) {
  return CONTRACT_DOC_TYPES[value] || value;
}

export function getContractDocTypeColor(value) {
  return CONTRACT_DOC_TYPE_COLORS[value] || CONTRACT_DOC_TYPE_COLORS.other;
}

export const CONTRACT_DOC_TYPE_OPTIONS = Object.entries(CONTRACT_DOC_TYPES).map(([value, label]) => ({
  value,
  label,
}));

// Helper functions
export function getContractTypeLabel(value) {
  return CONTRACT_TYPES[value] || value;
}

export function getEmployeeStatusLabel(value) {
  return EMPLOYEE_STATUS[value] || value;
}

export function getWorkTimeTypeLabel(value) {
  return WORK_TIME_TYPES[value] || value;
}

export function getTerminationReasonLabel(value) {
  return TERMINATION_REASONS[value] || value;
}

export function getFamilyStatusLabel(value) {
  if (!value) return "-";
  // Handle enum prefix from backend (e.g., "FamilyStatus.MARRIED" -> "married")
  const cleanValue = value.toString().replace(/^FamilyStatus\./, '').toLowerCase();
  return FAMILY_STATUS[cleanValue] || value;
}

export function getDocumentCategoryLabel(value) {
  return DOCUMENT_CATEGORIES[value] || value;
}

export function getDocumentCategoryColor(value) {
  return DOCUMENT_CATEGORY_COLORS[value] || DOCUMENT_CATEGORY_COLORS.other;
}

export function isFixedTermContract(contractType) {
  return FIXED_TERM_CONTRACTS.includes(contractType);
}

// Options pour les Select (format attendu par les composants)
export const CONTRACT_TYPE_OPTIONS = Object.entries(CONTRACT_TYPES).map(([value, label]) => ({
  value,
  label,
}));

export const EMPLOYEE_STATUS_OPTIONS = Object.entries(EMPLOYEE_STATUS).map(([value, label]) => ({
  value,
  label,
}));

export const WORK_TIME_OPTIONS = Object.entries(WORK_TIME_TYPES).map(([value, label]) => ({
  value,
  label,
}));

export const TERMINATION_REASON_OPTIONS = Object.entries(TERMINATION_REASONS).map(([value, label]) => ({
  value,
  label,
}));

export const FAMILY_STATUS_OPTIONS = Object.entries(FAMILY_STATUS).map(([value, label]) => ({
  value,
  label,
}));

export const DOCUMENT_CATEGORY_OPTIONS = Object.entries(DOCUMENT_CATEGORIES).map(([value, label]) => ({
  value,
  label,
}));


// ============================================================================
// PHASE 2: MEDICAL VISITS (Médecine du travail)
// ============================================================================

export const MEDICAL_VISIT_TYPES = {
  hiring: "Visite d'embauche",
  periodic: "Visite périodique",
  return_to_work: "Visite de reprise",
  pre_return: "Visite de pré-reprise",
  on_demand: "Visite à la demande",
  reinforced: "Suivi individuel renforcé",
};

export const MEDICAL_VISIT_STATUS = {
  scheduled: "Planifiée",
  completed: "Effectuée",
  cancelled: "Annulée",
  no_show: "Absent",
  postponed: "Reportée",
};

export const MEDICAL_FITNESS = {
  fit: "Apte",
  fit_with_restrictions: "Apte avec restrictions",
  temporarily_unfit: "Inapte temporaire",
  permanently_unfit: "Inapte définitif",
  pending: "En attente d'avis",
};

export const MEDICAL_VISIT_STATUS_COLORS = {
  scheduled: "bg-blue-100 text-blue-700 border-blue-200",
  completed: "bg-green-100 text-green-700 border-green-200",
  cancelled: "bg-slate-100 text-slate-700 border-slate-200",
  no_show: "bg-red-100 text-red-700 border-red-200",
  postponed: "bg-amber-100 text-amber-700 border-amber-200",
};

export const MEDICAL_FITNESS_COLORS = {
  fit: "bg-green-100 text-green-700 border-green-200",
  fit_with_restrictions: "bg-amber-100 text-amber-700 border-amber-200",
  temporarily_unfit: "bg-orange-100 text-orange-700 border-orange-200",
  permanently_unfit: "bg-red-100 text-red-700 border-red-200",
  pending: "bg-slate-100 text-slate-700 border-slate-200",
};

export function getMedicalVisitTypeLabel(value) {
  return MEDICAL_VISIT_TYPES[value] || value;
}

export function getMedicalVisitStatusLabel(value) {
  return MEDICAL_VISIT_STATUS[value] || value;
}

export function getMedicalFitnessLabel(value) {
  return MEDICAL_FITNESS[value] || value;
}

export function getMedicalVisitStatusColor(value) {
  return MEDICAL_VISIT_STATUS_COLORS[value] || MEDICAL_VISIT_STATUS_COLORS.scheduled;
}

export function getMedicalFitnessColor(value) {
  return MEDICAL_FITNESS_COLORS[value] || MEDICAL_FITNESS_COLORS.pending;
}

export const MEDICAL_VISIT_TYPE_OPTIONS = Object.entries(MEDICAL_VISIT_TYPES).map(([value, label]) => ({
  value,
  label,
}));

export const MEDICAL_VISIT_STATUS_OPTIONS = Object.entries(MEDICAL_VISIT_STATUS).map(([value, label]) => ({
  value,
  label,
}));

export const MEDICAL_FITNESS_OPTIONS = Object.entries(MEDICAL_FITNESS).map(([value, label]) => ({
  value,
  label,
}));

// ============================================================================
// PHASE 2: ABSENCES & LEAVES (Absences et Congés)
// ============================================================================

export const ABSENCE_TYPES = {
  paid_leave: "Congés payés",
  rtt: "RTT",
  unpaid_leave: "Congé sans solde",
  sick_leave: "Arrêt maladie",
  work_accident: "Accident du travail",
  maternity: "Congé maternité",
  paternity: "Congé paternité",
  parental: "Congé parental",
  family_event: "Événement familial",
  training: "Formation",
  remote_work: "Télétravail",
  other: "Autre",
};

export const ABSENCE_STATUS = {
  pending: "En attente",
  approved: "Approuvée",
  rejected: "Refusée",
  cancelled: "Annulée",
};

export const ABSENCE_TYPE_COLORS = {
  paid_leave: "bg-green-100 text-green-700 border-green-200",
  rtt: "bg-blue-100 text-blue-700 border-blue-200",
  unpaid_leave: "bg-slate-100 text-slate-700 border-slate-200",
  sick_leave: "bg-red-100 text-red-700 border-red-200",
  work_accident: "bg-red-100 text-red-700 border-red-200",
  maternity: "bg-pink-100 text-pink-700 border-pink-200",
  paternity: "bg-cyan-100 text-cyan-700 border-cyan-200",
  parental: "bg-purple-100 text-purple-700 border-purple-200",
  family_event: "bg-amber-100 text-amber-700 border-amber-200",
  training: "bg-indigo-100 text-indigo-700 border-indigo-200",
  remote_work: "bg-teal-100 text-teal-700 border-teal-200",
  other: "bg-gray-100 text-gray-700 border-gray-200",
};

export const ABSENCE_STATUS_COLORS = {
  pending: "bg-amber-100 text-amber-700 border-amber-200",
  approved: "bg-green-100 text-green-700 border-green-200",
  rejected: "bg-red-100 text-red-700 border-red-200",
  cancelled: "bg-slate-100 text-slate-700 border-slate-200",
};

export function getAbsenceTypeLabel(value) {
  return ABSENCE_TYPES[value] || value;
}

export function getAbsenceStatusLabel(value) {
  return ABSENCE_STATUS[value] || value;
}

export function getAbsenceTypeColor(value) {
  return ABSENCE_TYPE_COLORS[value] || ABSENCE_TYPE_COLORS.other;
}

export function getAbsenceStatusColor(value) {
  return ABSENCE_STATUS_COLORS[value] || ABSENCE_STATUS_COLORS.pending;
}

export const ABSENCE_TYPE_OPTIONS = Object.entries(ABSENCE_TYPES).map(([value, label]) => ({
  value,
  label,
}));

export const ABSENCE_STATUS_OPTIONS = Object.entries(ABSENCE_STATUS).map(([value, label]) => ({
  value,
  label,
}));


// ============================================================================
// PHASE 3: FORMATIONS (Trainings)
// ============================================================================

export const TRAINING_TYPES = {
  internal: "Formation interne",
  external: "Formation externe",
  e_learning: "E-learning",
  certification: "Certification",
  mandatory: "Obligatoire",
};

export const TRAINING_STATUS = {
  planned: "Planifiée",
  in_progress: "En cours",
  completed: "Terminée",
  cancelled: "Annulée",
};

export const TRAINING_STATUS_COLORS = {
  planned: "bg-blue-100 text-blue-700 border-blue-200",
  in_progress: "bg-amber-100 text-amber-700 border-amber-200",
  completed: "bg-green-100 text-green-700 border-green-200",
  cancelled: "bg-slate-100 text-slate-700 border-slate-200",
};

export function getTrainingTypeLabel(value) {
  return TRAINING_TYPES[value] || value;
}

export function getTrainingStatusLabel(value) {
  return TRAINING_STATUS[value] || value;
}

export function getTrainingStatusColor(value) {
  return TRAINING_STATUS_COLORS[value] || TRAINING_STATUS_COLORS.planned;
}

export const TRAINING_TYPE_OPTIONS = Object.entries(TRAINING_TYPES).map(([value, label]) => ({
  value,
  label,
}));

export const TRAINING_STATUS_OPTIONS = Object.entries(TRAINING_STATUS).map(([value, label]) => ({
  value,
  label,
}));

// ============================================================================
// PHASE 3: ENTRETIENS (Interviews)
// ============================================================================

export const INTERVIEW_TYPES = {
  annual: "Entretien annuel",
  professional: "Entretien professionnel",
  end_of_trial: "Fin de période d'essai",
  return_to_work: "Reprise après absence",
  career: "Entretien de carrière",
  disciplinary: "Entretien disciplinaire",
  exit: "Entretien de départ",
};

export const INTERVIEW_STATUS = {
  scheduled: "Planifié",
  completed: "Réalisé",
  cancelled: "Annulé",
  postponed: "Reporté",
};

export const INTERVIEW_STATUS_COLORS = {
  scheduled: "bg-blue-100 text-blue-700 border-blue-200",
  completed: "bg-green-100 text-green-700 border-green-200",
  cancelled: "bg-slate-100 text-slate-700 border-slate-200",
  postponed: "bg-amber-100 text-amber-700 border-amber-200",
};

export function getInterviewTypeLabel(value) {
  return INTERVIEW_TYPES[value] || value;
}

export function getInterviewStatusLabel(value) {
  return INTERVIEW_STATUS[value] || value;
}

export function getInterviewStatusColor(value) {
  return INTERVIEW_STATUS_COLORS[value] || INTERVIEW_STATUS_COLORS.scheduled;
}

export const INTERVIEW_TYPE_OPTIONS = Object.entries(INTERVIEW_TYPES).map(([value, label]) => ({
  value,
  label,
}));

export const INTERVIEW_STATUS_OPTIONS = Object.entries(INTERVIEW_STATUS).map(([value, label]) => ({
  value,
  label,
}));

// ============================================================================
// PHASE 3: ALERTES (Alerts)
// ============================================================================

export const ALERT_TYPES = {
  document_expiry: "Document expiré",
  contract_end: "Fin de contrat",
  trial_end: "Fin période d'essai",
  medical_visit_due: "Visite médicale",
  interview_due: "Entretien à planifier",
  training_due: "Formation obligatoire",
  leave_balance_low: "Solde congés bas",
};

export const ALERT_PRIORITY_COLORS = {
  critical: "bg-red-100 text-red-700 border-red-200",
  high: "bg-orange-100 text-orange-700 border-orange-200",
  medium: "bg-amber-100 text-amber-700 border-amber-200",
  low: "bg-blue-100 text-blue-700 border-blue-200",
};

export function getAlertTypeLabel(value) {
  return ALERT_TYPES[value] || value;
}

export function getAlertPriorityColor(value) {
  return ALERT_PRIORITY_COLORS[value] || ALERT_PRIORITY_COLORS.medium;
}
